import { useEffect, useRef, useState } from 'react'
import { createFrameSampler, estimateRefreshRate, summarizeFrameDeltas } from './performanceMetrics.js'
import { copyText } from './copyText.js'
import './PerformanceMonitor.css'
import { buildPerfTraceSnapshot, clearPerfTrace, recordPerfEvent } from '../../shared/lib/perf-trace'

export default function PerformanceMonitor({
  targetFps = 60,
  sampleSize = 120,
  updateMs = 500,
}) {
  const [stats, setStats] = useState(() => summarizeFrameDeltas([], targetFps))
  const [copyStatus, setCopyStatus] = useState(null)
  const frameRef = useRef(0)
  const lastUpdateTsRef = useRef(0)
  const longTasksRef = useRef({ count: 0, worstMs: 0 })

  useEffect(() => {
    const sampler = createFrameSampler(sampleSize)
    // rAF is suspended in a background tab; without a reset the whole hidden
    // interval would be sampled as a single frame.
    const onVisibilityChange = () => sampler.reset()
    document.addEventListener('visibilitychange', onVisibilityChange)

    const tick = (ts) => {
      sampler.push(ts)

      if (!lastUpdateTsRef.current || ts - lastUpdateTsRef.current >= updateMs) {
        lastUpdateTsRef.current = ts
        const longTasks = longTasksRef.current
        const detectedTargetFps = estimateRefreshRate(sampler.deltas, targetFps)
        const nextStats = {
          ...summarizeFrameDeltas(sampler.deltas, detectedTargetFps),
          targetFps: detectedTargetFps,
          longTaskCount: longTasks.count,
          worstLongTaskMs: longTasks.worstMs,
        }
        setStats(nextStats)
        recordPerfEvent('fps.sample', nextStats)
        longTasksRef.current = { count: 0, worstMs: 0 }
      }

      frameRef.current = requestAnimationFrame(tick)
    }

    frameRef.current = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(frameRef.current)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [sampleSize, targetFps, updateMs])

  useEffect(() => {
    if (typeof PerformanceObserver === 'undefined') return undefined
    let observer
    try {
      observer = new PerformanceObserver((list) => {
        const longTasks = longTasksRef.current
        for (const entry of list.getEntries()) {
          longTasks.count += 1
          longTasks.worstMs = Math.max(longTasks.worstMs, entry.duration)
        }
      })
      observer.observe({ entryTypes: ['longtask'] })
    } catch {
      return undefined
    }
    return () => observer?.disconnect()
  }, [])

  const fpsText = stats.fps ? Math.round(stats.fps) : '--'
  const lowText = stats.lowFps ? Math.round(stats.lowFps) : '--'
  const avgText = stats.avgMs ? stats.avgMs.toFixed(1) : '--'
  const targetText = stats.targetFps ? Math.round(stats.targetFps) : targetFps
  const longTaskText = stats.longTaskCount ? `${stats.longTaskCount} / ${Math.round(stats.worstLongTaskMs)}ms` : '0'

  const copyTrace = async () => {
    const payload = JSON.stringify(buildPerfTraceSnapshot({ stats }), null, 2)
    setCopyStatus(await copyText(payload) ? 'copied' : 'failed')
    setTimeout(() => setCopyStatus(null), 1200)
  }

  return (
    <div className={`perf-monitor ${stats.status}`} title="FPS / low FPS / average frame time / long tasks">
      <span className="perf-dot" />
      <span><b>{fpsText}</b> fps</span>
      <span>@{targetText}</span>
      <span>low {lowText}</span>
      <span>{avgText}ms</span>
      <span>long {longTaskText}</span>
      <button className="perf-action" type="button" onClick={copyTrace}>{copyStatus ?? 'copy'}</button>
      <button className="perf-action" type="button" onClick={clearPerfTrace}>clear</button>
    </div>
  )
}
